import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGlobal } from '@/context/GlobalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, User, ArrowLeft } from 'lucide-react';

const AdminLogin = () => {
  const { login } = useAuth();
  const { API } = useGlobal();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    admin_secret: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/admin/register' : '/admin/login';
      const payload = isRegister 
        ? formData 
        : { username: formData.username, password: formData.password };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.token, response.data.username);
      toast.success(isRegister ? 'Account created!' : 'Welcome back!');
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to website
        </Link>
        
        <div className="bg-white rounded-sm p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-100 rounded-sm flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#5b3589]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Montserrat']">
              {isRegister ? 'Create Admin Account' : 'Admin Login'}
            </h1>
            <p className="text-slate-600 text-sm mt-2">
              {isRegister ? 'Register a new admin account' : 'Access the Trinity CMS'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  required
                  placeholder="Enter username"
                  className="pl-10 rounded-sm"
                  data-testid="admin-username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  placeholder="Enter password"
                  className="pl-10 rounded-sm"
                  data-testid="admin-password"
                />
              </div>
            </div>

            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="admin_secret">Admin Secret</Label>
                <Input
                  id="admin_secret"
                  type="password"
                  value={formData.admin_secret}
                  onChange={(e) => setFormData(prev => ({ ...prev, admin_secret: e.target.value }))}
                  required
                  placeholder="Enter admin secret key"
                  className="rounded-sm"
                  data-testid="admin-secret"
                />
                <p className="text-xs text-slate-500">
                  Contact your administrator for the secret key
                </p>
              </div>
            )}

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#5b3589] hover:bg-[#4c1d95] text-white rounded-sm"
              data-testid="admin-submit"
            >
              {loading ? 'Processing...' : (isRegister ? 'Create Account' : 'Login')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#5b3589] text-sm hover:underline"
            >
              {isRegister ? 'Already have an account? Login' : 'Need an account? Register'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
